<template>
  <div class="relation-setter rs-columns" :class="className">

    <!-- Unselected Items -->
    <div class="relation-setter__options rs-column">
      <label>{{ unselectedLabel | sentenceCase }}</label>
      <div class="list-wrapper">
        <div class="search">
          <BaseIcon class="search__loupe" name="loupe"/>
          <input type="search" :placeholder="placeholder" v-model="searchQuery">
        </div>
        
        <LazyList :items="unselected" class="list list--with-search" :class="scrollAreaClassName" :scrollTopKey="searchQuery">
          <template slot-scope="props">
            <RelationListItem
              v-for="item in props.items"
              :key="item[key_uid]"
              :label="item[key_label]"
              :image="getItemImage(item)"
              :popoverData="getPopoverData(item)"
              >
              <BaseButton slot="selectionHandler" class="relation-list-item__btn" icon="circle-plus-outline" :useImgNotSvg="true" @click.native="handleSelection(item)"/>
            </RelationListItem>
          </template>
        </LazyList>

        <TypographyInputHint v-if="showNoItemsMsg" class="relation-setter__no-items">No items.</TypographyInputHint>
      
      </div>

      <!-- Loader -->
      <BaseSpinnerBig class="relation-setter__spinner" v-if="loading"/>
    </div>

    <!-- Selected Items -->
    <div class="relation-setter__selected rs-column">
      <label>{{ selectedLabel | sentenceCase }}</label>
      <div class="list-wrapper">
        <ul class="list" :class="scrollAreaClassName">
          <template v-if="value && value.length">
            <draggable :list="value">
              <RelationListItem
                v-for="item in value"
                :key="item[key_uid]"
                :label="item[key_label]"
                :image="getItemImage(item)"
                :popoverData="getPopoverData(item)"
                :showDragHandle="true"
                >
                <BaseButton slot="selectionHandler" class="relation-list-item__btn" icon="trash" :useImgNotSvg="true" @click.native="handleDeselection(item)"/>
              </RelationListItem>
            </draggable>
          </template>
        </ul>
      </div>
    </div>

  </div>
</template>

<script>
  import { get, isFunction } from 'lodash'
  import RelationListItem, { scrollAreaClassName } from '@/components/RelationListItem'
  import LazyList from '@/components/LazyList'
  import { PopoverDataModel } from '@/models/popover-data.model'
  import { getSearchbarPlaceholder } from '@/helpers'
  import draggable from 'vuedraggable'
  
  export default {
    components: {
      RelationListItem,
      draggable,
      LazyList,
    },

    props: {
      items: Array,
      value: Array,
      unselectedLabel: { type: String, default: 'Select similar products' },
      selectedLabel: { type: String, default: 'Selected products' }, 
      loading: Boolean,
      linkUrl: {
        type: [ String, Function ],
        default: '#'
      },
      key_uid: { type: String, default: 'key' },
      key_img: { type: String, default: 'featured_image' },
      key_label: { type: String, default: 'label' },
      key_img_second: { type: String, default: '' },
    },
    
    data () {
      return {
        searchQuery: '',
        scrollAreaClassName
      }
    },

    computed: {
      className () {
        return {
          'relation-setter--loading': this.loading
        }
      },
      isValueExists () {
        return get(this.value, 'length')
      },
      isItemsExists () {
        return get(this.items, 'length')
      },
      itemsForSearch () {
        if (Array.isArray(this.items)) {
          const res = []
          for (let i = 0; i < this.items.length; i++) {
            const item = this.items[i];
            const str = (item.handle || '') + ' ' + (item.id || '') + ' ' + (item[this.key_label] || '') + ' ' + (item.description || '')
            res.push(str.toLowerCase())
          }
          return res
        } else {
          return []
        }
      },
      queriedItems () {
        if (this.searchQuery) {
          const results = []
          const query = this.searchQuery.toLowerCase().trim()
          const indexes = []

          for (let i = 0; i < this.itemsForSearch.length; i++) {
            if (this.itemsForSearch[i].indexOf(query) > -1) {
              indexes.push(i)
            }
          }

          for (let i = 0; i < indexes.length; i++) {
            const idx = indexes[i]
            results.push(this.items[idx])
          }

          return results
        } else {
          return this.items || []
        }
      },
      unselected () {
        if (this.isValueExists) {
          const _value = [ ...this.value ]

          return this.queriedItems.filter(a => {
            const findIndex = this.value.findIndex(b => a[this.key_uid] === b[this.key_uid])

            if (findIndex > -1) {
              // Remove finded item from _value for optimization
              _value.splice(findIndex, 1)
              return false
            }

            return true
          })
        } else {
          return this.queriedItems
        }
      },
      placeholder () {
        return getSearchbarPlaceholder({ item: (this.items || [])[0] })
      },
      showNoItemsMsg () {
        return !this.loading && (!this.unselected || !this.unselected.length)
      }
    },

    methods: {
      handleSelection (el) {
        let payload

        if (this.isValueExists) {
          payload = [ ...this.value, el]
        } else {
          payload = [ el ]
        }

        this.$emit('input', payload)
      },
      handleDeselection (el) {
        const payload = this.value.filter(item => item[this.key_uid] !== el[this.key_uid])
        this.$emit('input', payload)
      },
      url (item) {
        return isFunction(this.linkUrl) ? this.linkUrl(item) : this.linkUrl
      },
      convertValue () {
        // This function converts value from <Array of String/Numbers> to <Array of Objects>.
        if (!this.isValueExists || !this.isItemsExists) return
        // Run only if value is array of strings
        if (typeof this.value[0] === 'object') return

        const filteredItems = this.items.filter(item => {
          return this.value.includes(item[this.key_uid])
        })

        const payload = this.value.map(handle => {
          return filteredItems.find(item => item[this.key_uid] === handle)
        }).filter(Boolean)

        this.$emit('input', payload)
      },
      getItemImage (item) {
        if (item[this.key_img] && !this.key_img_second) {
          return item[this.key_img]
        } else if (item[this.key_img] && this.key_img_second) {
          return item[this.key_img][this.key_img_second]
        } else {
          return ''
        }
      },
      getPopoverData (item) {
        return new PopoverDataModel({
          label: item[this.key_label],
          id: item.id,
          handle: item.handle,
          description: item.description,
        })
      }
    },

    watch: {
      value: {
        handler () {        
          this.convertValue()
        },
        immediate: true
      },
      items: {
        handler () {
          this.convertValue()
        },
        immediate: true
      },
    },
  }
</script>

<style lang="scss">
  @import './src/assets/styles/colors.scss';
  @import './src/assets/styles/mixins.scss';
  @import './src/assets/styles/breakpoints.scss';

  .relation-setter {
    
    label {
      display: block;
      margin-bottom: 5px;
      @include base-field-title;
    }

    .search {
      display: flex;
      align-items: center;
      height: 32px;
      padding: 7px 10px;
      border-bottom: 1px solid $color-porcelain;
      color: $color-regent-gray;
      font-size: 14px;

      input {
        flex-grow: 1;
      }

      input::placeholder {
        color: $color-regent-gray;
      }

      &__loupe {
        width: 14px;
        height: 14px;
        margin-right: 12px;
      }
    }

    &__options {
      position: relative;
    }

    &__spinner {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &__no-items {
      text-align: center;
    }
    
    .list-wrapper {
      height: 335px;
      border: 1px solid $color-loblolly;
      border-radius: 3px;
      overflow: hidden;
    }

    .list {
      height: 100%;
      overflow-x: hidden;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      transition: opacity .7s;

      &--with-search {
        height: calc(100% - 32px);
      }
    }

    &--loading {
      .list {
        opacity: 0;
      }
    }
  }


  .rs-columns {
    display: flex;
    justify-content: space-between;

    @include media(ltMobile) {
      display: block;
    }

    .rs-column {
      width: calc(50% - 5px);

      @include media(ltMobile) {
        width: 100%;
        
        &:first-of-type {
          margin-bottom: 20px;
        }
      }
    }
  }
</style>


// WEBPACK FOOTER //
// src/components/RelationSetter.vue